import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { ideasAPI, uploadFile } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ArrowLeft, Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Inventory Management',
  'Procurement',
  'Sales Analytics',
  'AI GM',
  'AI Booking',
  'Staffty HR',
  'New Module',
  'Other',
];

const PRIORITIES = [
  { value: 'critical', label: 'Critical', description: 'Blocking work', color: 'text-red-600' },
  { value: 'high', label: 'High', description: 'Needed, have workarounds', color: 'text-orange-600' },
  { value: 'medium', label: 'Medium', description: 'Nice to have', color: 'text-yellow-600' },
  { value: 'low', label: 'Low', description: 'Future consideration', color: 'text-gray-600' },
];

export default function IdeaSubmitPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    problem_description: '',
    proposed_solution: '',
    business_impact: '',
    priority: 'medium',
  });

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const validFiles = newFiles.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    if (files.length + validFiles.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }
    
    setFiles([...files, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();
    
    if (!formData.title || !formData.problem_description || 
        !formData.proposed_solution || !formData.business_impact) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const ideaData = {
        ...formData,
        status: isDraft ? 'draft' : 'submitted',
      };
      
      const res = await ideasAPI.create(ideaData);
      const ideaId = res.data.id;
      
      // Upload files
      for (const file of files) {
        await uploadFile(file, null, ideaId);
      }
      
      toast.success(isDraft ? 'Idea saved as draft' : 'Idea submitted successfully!');
      navigate('/ideas');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit idea');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'restaurant' && user?.role !== 'admin' && user?.role !== 'partner') {
    return (
      <DashboardLayout pageTitle="Submit Idea">
        <div className="text-center py-12">
          <p className="text-[#666666]">Only restaurant and partner users can submit ideas</p>
          <Button onClick={() => navigate('/ideas')} className="mt-4">
            Back to Ideas
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Submit Idea">
      <div className="max-w-2xl mx-auto" data-testid="submit-idea-page">
        <Button variant="ghost" onClick={() => navigate('/ideas')} className="mb-4">
          <ArrowLeft size={16} className="mr-2" />
          Back to Ideas
        </Button>

        <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
          <h2 className="text-xl font-bold text-[#111111] mb-6">Submit a New Idea</h2>
          
          <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
            {/* Title */}
            <div>
              <Label htmlFor="title">
                Idea Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Add OCR invoice scanning"
                maxLength={100}
                data-testid="idea-title"
              />
              <p className="text-xs text-[#666666] mt-1">{formData.title.length}/100 characters</p>
            </div>

            {/* Category - hidden for restaurant users */}
            {user?.role !== 'restaurant' && (
            <div>
              <Label>
                Category
              </Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData(f => ({ ...f, category: v }))}
              >
                <SelectTrigger data-testid="idea-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Problem Description */}
            <div>
              <Label htmlFor="problem">
                What problem does this solve? <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="problem"
                value={formData.problem_description}
                onChange={(e) => setFormData(f => ({ ...f, problem_description: e.target.value }))}
                placeholder="Be specific: frequency, time spent, current workarounds"
                rows={4}
                maxLength={2500}
                data-testid="idea-problem"
              />
              <p className="text-xs text-[#666666] mt-1">{formData.problem_description.length}/2500 characters</p>
            </div>

            {/* Proposed Solution */}
            <div>
              <Label htmlFor="solution">
                How would you solve this? <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="solution"
                value={formData.proposed_solution}
                onChange={(e) => setFormData(f => ({ ...f, proposed_solution: e.target.value }))}
                placeholder="Describe desired functionality and expected outcome"
                rows={4}
                maxLength={2500}
                data-testid="idea-solution"
              />
              <p className="text-xs text-[#666666] mt-1">{formData.proposed_solution.length}/2500 characters</p>
            </div>

            {/* Business Impact */}
            <div>
              <Label htmlFor="impact">
                What value would this bring? <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="impact"
                value={formData.business_impact}
                onChange={(e) => setFormData(f => ({ ...f, business_impact: e.target.value }))}
                placeholder="Estimate time saved, revenue impact, error reduction"
                rows={3}
                maxLength={1500}
                data-testid="idea-impact"
              />
              <p className="text-xs text-[#666666] mt-1">{formData.business_impact.length}/1500 characters</p>
            </div>

            {/* Priority */}
            <div>
              <Label>
                Priority <span className="text-red-500">*</span>
              </Label>
              <RadioGroup
                value={formData.priority}
                onValueChange={(v) => setFormData(f => ({ ...f, priority: v }))}
                className="mt-2 space-y-2"
              >
                {PRIORITIES.map(p => (
                  <div key={p.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={p.value} id={p.value} />
                    <Label htmlFor={p.value} className="flex items-center gap-2 cursor-pointer">
                      <span className={`font-medium ${p.color}`}>{p.label}</span>
                      <span className="text-sm text-[#666666]">({p.description})</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* File Upload */}
            <div>
              <Label>Attachments (optional, max 5 files, 10MB each)</Label>
              <div className="mt-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  <div className="border-2 border-dashed border-[#E0E0E0] rounded-lg p-6 text-center hover:border-[#FF6B35] transition-colors">
                    <Upload className="mx-auto text-[#666666] mb-2" size={24} />
                    <p className="text-sm text-[#666666]">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-[#999999] mt-1">
                      Images, PDFs, Documents
                    </p>
                  </div>
                </label>
              </div>
              
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div key={`${file.name}-${file.size}`} className="flex items-center justify-between bg-[#F5F5F5] rounded p-2">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-[#666666]" />
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-[#999999]">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white flex-1"
                data-testid="submit-idea-btn"
              >
                {loading ? 'Submitting...' : 'Submit Idea'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading}
              >
                Save as Draft
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
